import { useEffect, useState } from "react";
import { Users, Building2, Plus, Trash2, ShieldCheck, MapPin, BarChart3, History, IndianRupee, Download } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";
import api, { formatApiError, downloadFile } from "@/lib/api";
import { PageHeader, SectionCard } from "@/components/ui-helpers";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePicker } from "@/components/DatePicker";
import { toast } from "sonner";

export default function TeamPage() {
  const [d, setD] = useState(null);
  const [bOpen, setBOpen] = useState(false);
  const [sOpen, setSOpen] = useState(false);
  const [branch, setBranch] = useState({ name: "", city: "" });
  const [staff, setStaff] = useState({ name: "", email: "", password: "", role: "staff", branch_id: "", branch_ids: [] });
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState(null);
  const [af, setAf] = useState({ branch_id: "all", actor: "all", from: "", to: "", q: "" });
  const [digest, setDigest] = useState({ mode: "weekly", day: "0" });

  const load = () => api.get("/team/overview").then((r) => setD(r.data)).catch(() => setD({ is_owner: false, branches: [], staff: [] }));
  useEffect(() => {
    load();
    api.get("/team/branch-stats").then((r) => setStats(r.data.stats)).catch(() => {});
    api.get("/team/activity").then((r) => setActivity(r.data.activity)).catch(() => {});
  }, []);

  const _afQuery = (f, extra = {}) => {
    const p = new URLSearchParams(extra);
    if (f.branch_id && f.branch_id !== "all") p.set("branch_id", f.branch_id);
    if (f.actor && f.actor !== "all") p.set("actor", f.actor);
    if (f.from) p.set("date_from", f.from);
    if (f.to) p.set("date_to", f.to);
    if (f.q) p.set("q", f.q);
    return p.toString();
  };
  const changeAf = (k, v) => {
    const n = { ...af, [k]: v };
    setAf(n);
    api.get(`/team/activity?${_afQuery(n)}`).then((r) => setActivity(r.data.activity)).catch(() => {});
  };
  const exportActivityCsv = () => downloadFile(`/team/activity?${_afQuery(af, { fmt: "csv" })}`, "team_activity.csv").catch(() => toast.error("Could not export"));
  const exportBranchCsv = () => {
    const rows = [["Branch", "City", "Enquiries", "Quotations", "Accepted", "Revenue"], ...stats.map((s) => [s.name, s.city, s.enquiries, s.quotations, s.accepted, s.revenue])];
    const csv = rows.map((r) => r.map((x) => `"${x}"`).join(",")).join("\n");
    const u = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = u; a.download = "branch_comparison.csv"; a.click(); URL.revokeObjectURL(u);
  };
  const members = [{ id: d?.agency_id, name: "Owner (you)" }, ...((d?.staff) || [])];
  const saveDigest = async (next) => {
    setDigest(next);
    try { await api.put("/me/digest-preference", { mode: next.mode, day: Number(next.day) }); toast.success("Digest preference saved"); }
    catch (e) { toast.error("Could not save preference"); }
  };
  const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
  const sendDigestPreview = async () => {
    try { const { data } = await api.post("/team/digest-preview"); toast.success("Test digest sent", { description: `Check ${data.sent_to}` }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not send test digest"); }
  };

  const addBranch = async () => {
    if (!branch.name.trim()) { toast.error("Branch name is required."); return; }
    try { await api.post("/team/branches", branch); toast.success("Branch added"); setBOpen(false); setBranch({ name: "", city: "" }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delBranch = async (id) => {
    if (!window.confirm("Delete this branch? Staff must be reassigned or removed first.")) return;
    try { await api.delete(`/team/branches/${id}`); toast.success("Branch removed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const addStaff = async () => {
    const okBranch = staff.role === "manager" ? staff.branch_ids.length > 0 : staff.role === "viewer" ? true : !!staff.branch_id;
    if (!staff.name.trim() || !staff.email.trim() || !staff.password || !okBranch) { toast.error("All fields are required (pick at least one branch)."); return; }
    try { await api.post("/team/staff", staff); toast.success(`${staff.role === "manager" ? "Manager" : staff.role === "viewer" ? "Viewer" : "Staff member"} added`); setSOpen(false); setStaff({ name: "", email: "", password: "", role: "staff", branch_id: "", branch_ids: [] }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleMgrBranch = (id) => setStaff((s) => ({ ...s, branch_ids: s.branch_ids.includes(id) ? s.branch_ids.filter((x) => x !== id) : [...s.branch_ids, id] }));
  const delStaff = async (id) => {
    if (!window.confirm("Remove this staff member? Their login will be permanently disabled.")) return;
    try { await api.delete(`/team/staff/${id}`); toast.success("Staff member removed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const isOwner = d?.is_owner;

  return (
    <div>
      <PageHeader title="Team & Branches" icon={Users}
        subtitle="Add branches and staff. Staff see only their branch's records; you see everything across your store." />

      {d && !isOwner && (
        <div className="mb-5 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700" data-testid="team-staff-notice">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" /> You're a staff member. Only the store owner can manage branches and team members.
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-2">
        <SectionCard title="Branches" testid="team-branches"
          action={isOwner && <button onClick={() => setBOpen(true)} data-testid="add-branch-btn" className="inline-flex items-center gap-1.5 rounded-full brand-gradient-bg px-3.5 py-1.5 text-xs font-semibold text-white"><Plus className="h-3.5 w-3.5" /> Add Branch</button>}>
          {d?.branches?.length ? d.branches.map((b) => (
            <div key={b.branch_id} className="flex items-center gap-3 border-b border-slate-50 py-2.5 last:border-0" data-testid="branch-item">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50 text-blue-500"><Building2 className="h-4 w-4" /></span>
              <div className="min-w-0 flex-1"><p className="truncate text-sm font-medium text-slate-700">{b.name}</p><p className="truncate text-xs text-slate-400 flex items-center gap-1"><MapPin className="h-3 w-3" /> {b.city || "—"}</p></div>
              {isOwner && <button onClick={() => delBranch(b.branch_id)} data-testid="delete-branch-btn" className="rounded-lg p-1.5 text-slate-300 hover:bg-rose-50 hover:text-rose-500"><Trash2 className="h-4 w-4" /></button>}
            </div>
          )) : <p className="py-4 text-sm text-slate-400">No branches yet. Add your first branch to organise your team.</p>}
        </SectionCard>

        <SectionCard title="Staff" testid="team-staff"
          action={isOwner && <button onClick={() => { if (!d?.branches?.length) { toast.error("Add a branch first."); return; } setSOpen(true); }} data-testid="add-staff-btn" className="inline-flex items-center gap-1.5 rounded-full brand-gradient-bg px-3.5 py-1.5 text-xs font-semibold text-white"><Plus className="h-3.5 w-3.5" /> Add Staff</button>}>
          {d?.staff?.length ? d.staff.map((s) => (
            <div key={s.id} className="flex items-center gap-3 border-b border-slate-50 py-2.5 last:border-0" data-testid="staff-item">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-purple-50 text-xs font-semibold text-purple-500">{s.name?.[0]?.toUpperCase()}</span>
              <div className="min-w-0 flex-1"><p className="truncate text-sm font-medium text-slate-700">{s.name}{s.role === "manager" && <span className="ml-2 rounded-full bg-purple-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-purple-600">Manager</span>}{s.role === "viewer" && <span className="ml-2 rounded-full bg-slate-200 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-slate-600">Read-only</span>}</p><p className="truncate text-xs text-slate-400">{s.email} · {s.branch_name}</p></div>
              {isOwner && <button onClick={() => delStaff(s.id)} data-testid="delete-staff-btn" className="rounded-lg p-1.5 text-slate-300 hover:bg-rose-50 hover:text-rose-500"><Trash2 className="h-4 w-4" /></button>}
            </div>
          )) : <p className="py-4 text-sm text-slate-400">No staff yet. Add team members and assign them to a branch.</p>}
        </SectionCard>
      </div>

      <Dialog open={bOpen} onOpenChange={setBOpen}>
        <DialogContent data-testid="branch-dialog">
          <DialogHeader><DialogTitle>Add Branch</DialogTitle><DialogDescription>Create a branch office. You can assign staff and managers to it.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Branch Name</Label><Input value={branch.name} onChange={(e) => setBranch({ ...branch, name: e.target.value })} data-testid="branch-name-input" placeholder="Guwahati Branch" className="mt-1" /></div>
            <div><Label className="text-xs">City</Label><Input value={branch.city} onChange={(e) => setBranch({ ...branch, city: e.target.value })} data-testid="branch-city-input" placeholder="Guwahati" className="mt-1" /></div>
          </div>
          <button onClick={addBranch} data-testid="branch-save-btn" className="mt-2 w-full rounded-xl brand-gradient-bg py-2.5 font-semibold text-white">Add Branch</button>
        </DialogContent>
      </Dialog>

      <Dialog open={sOpen} onOpenChange={setSOpen}>
        <DialogContent data-testid="staff-dialog">
          <DialogHeader><DialogTitle>Add Staff Member</DialogTitle><DialogDescription>Create a login for a team member and set their access level.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Full Name</Label><Input value={staff.name} onChange={(e) => setStaff({ ...staff, name: e.target.value })} data-testid="staff-name-input" className="mt-1" /></div>
            <div><Label className="text-xs">Email</Label><Input type="email" value={staff.email} onChange={(e) => setStaff({ ...staff, email: e.target.value })} data-testid="staff-email-input" className="mt-1" /></div>
            <div><Label className="text-xs">Temporary Password</Label><Input type="text" value={staff.password} onChange={(e) => setStaff({ ...staff, password: e.target.value })} data-testid="staff-password-input" placeholder="min 6 characters" className="mt-1" /></div>
            <div><Label className="text-xs">Role</Label>
              <Select value={staff.role} onValueChange={(v) => setStaff({ ...staff, role: v, branch_id: "", branch_ids: [] })}>
                <SelectTrigger data-testid="staff-role-select" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="staff">Staff (single branch)</SelectItem><SelectItem value="manager">Manager (oversees several branches)</SelectItem><SelectItem value="viewer">Viewer (read-only, all branches)</SelectItem></SelectContent>
              </Select>
            </div>
            {staff.role === "manager" ? (
              <div><Label className="text-xs">Branches this manager oversees</Label>
                <div className="mt-1 space-y-1.5 rounded-lg border border-slate-100 p-2" data-testid="staff-branches-multi">
                  {(d?.branches || []).map((b) => (
                    <label key={b.branch_id} className="flex cursor-pointer items-center gap-2 text-sm text-slate-600">
                      <input type="checkbox" checked={staff.branch_ids.includes(b.branch_id)} onChange={() => toggleMgrBranch(b.branch_id)} data-testid={`staff-mgr-branch-${b.branch_id}`} className="h-4 w-4 rounded border-slate-300" />
                      {b.name}
                    </label>
                  ))}
                </div>
              </div>
            ) : staff.role === "viewer" ? (
              <div className="rounded-lg border border-slate-100 bg-slate-50 p-3 text-xs text-slate-500" data-testid="staff-viewer-note">
                Viewers can see all agency records and dashboards but cannot create, edit or delete anything.
              </div>
            ) : (
              <div><Label className="text-xs">Branch</Label>
                <Select value={staff.branch_id} onValueChange={(v) => setStaff({ ...staff, branch_id: v })}>
                  <SelectTrigger data-testid="staff-branch-select" className="mt-1"><SelectValue placeholder="Select a branch" /></SelectTrigger>
                  <SelectContent>{(d?.branches || []).map((b) => <SelectItem key={b.branch_id} value={b.branch_id}>{b.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
          </div>
          <button onClick={addStaff} data-testid="staff-save-btn" className="mt-2 w-full rounded-xl brand-gradient-bg py-2.5 font-semibold text-white">Create Staff Login</button>
          <p className="mt-2 text-[11px] text-slate-400">Share these credentials with your staff member. They can sign in and work within their branch immediately.</p>
        </DialogContent>
      </Dialog>
      {stats && stats.length > 0 && (
        <div className="mt-5" data-testid="branch-comparison">
          <SectionCard title="Branch Comparison" icon={BarChart3}
            action={<button onClick={exportBranchCsv} data-testid="branch-csv-btn" className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-xs font-medium text-slate-600 hover:border-emerald-300 hover:text-emerald-600"><Download className="h-3.5 w-3.5" /> CSV</button>}>
            {stats.some((s) => s.revenue > 0) && (
              <div className="mb-4 h-56" data-testid="branch-revenue-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={stats.map((s) => ({ name: s.name, revenue: s.revenue }))}>
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#94a3b8" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} width={64} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v) => `₹${Number(v).toLocaleString("en-IN")}`} />
                    <Bar dataKey="revenue" fill="#7C3AED" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-slate-100 text-left text-xs text-slate-400">
                  <th className="py-2">Branch</th><th className="py-2">Enquiries</th><th className="py-2">Quotations</th><th className="py-2">Accepted</th><th className="py-2 text-right">Revenue</th>
                </tr></thead>
                <tbody>
                  {stats.map((s) => (
                    <tr key={s.branch_id} className="border-b border-slate-50" data-testid="branch-stat-row">
                      <td className="py-2 font-medium text-slate-700">{s.name}<span className="ml-2 text-xs text-slate-400">{s.city}</span></td>
                      <td className="py-2 text-slate-600">{s.enquiries}</td>
                      <td className="py-2 text-slate-600">{s.quotations}</td>
                      <td className="py-2 text-slate-600">{s.accepted}</td>
                      <td className="py-2 text-right font-semibold text-slate-800"><IndianRupee className="mr-0.5 inline h-3 w-3" />{s.revenue.toLocaleString("en-IN")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-[11px] text-slate-400">Revenue reflects only accepted quotations per branch — never fabricated.</p>
          </SectionCard>
        </div>
      )}

      {isOwner && activity && (
        <div className="mt-5" data-testid="team-activity">
          <SectionCard title="Team Activity" icon={History}
            action={<button onClick={exportActivityCsv} data-testid="activity-csv-btn" className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-xs font-medium text-slate-600 hover:border-emerald-300 hover:text-emerald-600"><Download className="h-3.5 w-3.5" /> CSV</button>}>
            <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="activity-filters">
              <Input placeholder="Search by title…" value={af.q} onChange={(e) => changeAf("q", e.target.value)} data-testid="activity-search" className="col-span-2 h-9 text-xs sm:col-span-4" />
              <Select value={af.branch_id} onValueChange={(v) => changeAf("branch_id", v)}>
                <SelectTrigger className="h-9 text-xs" data-testid="activity-branch-filter"><SelectValue placeholder="Branch" /></SelectTrigger>
                <SelectContent><SelectItem value="all">All branches</SelectItem>{(d?.branches || []).map((b) => <SelectItem key={b.branch_id} value={b.branch_id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={af.actor} onValueChange={(v) => changeAf("actor", v)}>
                <SelectTrigger className="h-9 text-xs" data-testid="activity-actor-filter"><SelectValue placeholder="Person" /></SelectTrigger>
                <SelectContent><SelectItem value="all">Everyone</SelectItem>{members.filter((m) => m.id).map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
              </Select>
              <DatePicker value={af.from} onChange={(v) => changeAf("from", v)} placeholder="From" testid="activity-from" className="h-9 text-xs" clearable />
              <DatePicker value={af.to} onChange={(v) => changeAf("to", v)} placeholder="To" testid="activity-to" className="h-9 text-xs" clearable />
            </div>
            {activity.length === 0 ? (
              <p className="py-6 text-center text-sm text-slate-400">No activity matches these filters.</p>
            ) : activity.slice(0, 30).map((a, i) => (
              <div key={`${a.type}-${a.title}-${a.updated_at || a.created_at || i}`} className="flex items-center gap-3 border-b border-slate-50 py-2.5 last:border-0" data-testid="activity-item">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-slate-700"><span className="capitalize text-slate-400">{a.type}</span> · {a.title}</p>
                  <p className="truncate text-xs text-slate-400">{a.branch} · created by {a.created_by}{a.updated_by ? ` · last edited by ${a.updated_by}` : ""}</p>
                </div>
                <span className="shrink-0 text-[11px] text-slate-400">{a.updated_at ? new Date(a.updated_at).toLocaleDateString("en-GB") : ""}</span>
              </div>
            ))}
          </SectionCard>
        </div>
      )}
      {isOwner && (
        <div className="mt-5" data-testid="digest-pref">
          <SectionCard title="Weekly Digest Email">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm text-slate-500">Email me a team performance summary</span>
              <Select value={digest.mode} onValueChange={(v) => saveDigest({ ...digest, mode: v })}>
                <SelectTrigger className="h-9 w-[130px] text-xs" data-testid="digest-mode"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="weekly">Weekly</SelectItem><SelectItem value="monthly">Monthly (1st)</SelectItem><SelectItem value="off">Off</SelectItem></SelectContent>
              </Select>
              {digest.mode === "weekly" && (
                <Select value={digest.day} onValueChange={(v) => saveDigest({ ...digest, day: v })}>
                  <SelectTrigger className="h-9 w-[150px] text-xs" data-testid="digest-day"><SelectValue /></SelectTrigger>
                  <SelectContent>{DAYS.map((d2, i) => <SelectItem key={d2} value={String(i)}>Every {d2}</SelectItem>)}</SelectContent>
                </Select>
              )}
              <button onClick={sendDigestPreview} data-testid="digest-preview-btn" className="rounded-full border border-indigo-300 px-4 py-2 text-xs font-semibold text-indigo-600 transition hover:bg-indigo-50">Send me a test</button>
            </div>
          </SectionCard>
        </div>
      )}
    </div>
  );
}
